<template>
  <div class="form-section">
    <vue-headful :title="pageTitle"/>

    <div class="container">
      <div class="block">
        <div class="content">
          <message-alert :show="!!status" :message="status" :is-success="alertType === 'success'"/>

          <h2>{{ pageTitle }}</h2>
          <hr>

          <div v-if="docsStatus === 'clarification_required' && rejectionNote" class="alert alert-warning" style="border-left: 4px solid #ffc107;">
            <strong>Требуется уточнение по документам:</strong>
            <p class="mb-0 mt-1">{{ rejectionNote }}</p>
          </div>

          <form @submit.prevent="onSubmit" class="padding40">
            <fieldset>
              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="control-label">Субъект организации</label>
                    <div class="input-group mb-3" style="display: flex">
                      <nationality-dropdown
                        :selected-id="form.nationality_id"
                        :disabled="formDisabled"
                        @nationality-selected="form.nationality_id = $event; clearFieldError('nationality_id')"
                      />
                      <base-select
                        v-model="form.subject_id"
                        :items="subjects"
                        input-label=""
                        :disabled="formDisabled"
                        input-name="subject_id"
                        @change="clearFieldError('subject_id')"
                      />
                    </div>
                    <small v-if="errors.nationality_id" class="text-danger">{{ errors.nationality_id[0] }}</small>
                    <small v-if="errors.subject_id" class="text-danger">{{ errors.subject_id[0] }}</small>
                  </div>
                </div>
                <div class="col-md-8">
                  <organization-name-input
                    v-model="form.name"
                    :subject-id="form.subject_id"
                    :nationality-id="form.nationality_id"
                    :nationalities-list="nationalitiesList"
                    :location-name="locationName"
                    :disabled="disabledName"
                    :input-label="nameLabel"
                    @typeahead-selected="selectTypeaheadOrganization($event)"
                    @switch-location-name="switchLocationName"
                    @change="handleNameChange"
                  />
                  <small v-if="errors.name" class="text-danger">{{ errors.name[0] }}</small>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label for="full_name">Полное наименование</label>
                    <input
                      id="full_name"
                      v-model="form.full_name"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('full_name')"
                    >
                    <small v-if="errors.full_name" class="text-danger">{{ errors.full_name[0] }}</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="ogrn">ОГРН</label>
                    <input
                      id="ogrn"
                      v-model="form.ogrn"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('ogrn')"
                    >
                    <small v-if="errors.ogrn" class="text-danger">{{ errors.ogrn[0] }}</small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="inn">ИНН</label>
                    <input
                      id="inn"
                      v-model="form.inn"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('inn')"
                    >
                    <small v-if="errors.inn" class="text-danger">{{ errors.inn[0] }}</small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="kpp">КПП</label>
                    <input
                      id="kpp"
                      v-model="form.kpp"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('kpp')"
                    >
                    <small v-if="errors.kpp" class="text-danger">{{ errors.kpp[0] }}</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="registration_date">Дата регистрации</label>
                    <date-picker
                      v-model="form.registration_date"
                      format="DD.MM.YYYY"
                      value-type="YYYY-MM-DD"
                      :disabled="formDisabled"
                      @change="clearFieldError('registration_date')"
                    />
                    <small v-if="errors.registration_date" class="text-danger">{{ errors.registration_date[0] }}</small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="okved">ОКВЭД</label>
                    <input
                      id="okved"
                      v-model="form.okved"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('okved')"
                    >
                    <small v-if="errors.okved" class="text-danger">{{ errors.okved[0] }}</small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="okpo">ОКПО</label>
                    <input
                      id="okpo"
                      v-model="form.okpo"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('okpo')"
                    >
                    <small v-if="errors.okpo" class="text-danger">{{ errors.okpo[0] }}</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-8">
                  <div class="form-group">
                    <label for="juristic_address">Юридический адрес</label>
                    <input
                      id="juristic_address"
                      v-model="form.juristic_address"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('juristic_address')"
                    >
                    <small v-if="errors.juristic_address" class="text-danger">{{ errors.juristic_address[0] }}</small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="region_id">Регион</label>
                    <select
                      id="region_id"
                      v-model="form.region_id"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('region_id')"
                    >
                      <option v-for="(name, region_id) in regionsList" :key=region_id :value="region_id">
                        {{ name }}
                      </option>
                    </select>
                    <small v-if="errors.region_id" class="text-danger">{{ errors.region_id[0] }}</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-8">
                  <div class="form-group">
                    <label for="address">Фактический адрес</label>
                    <input
                      id="address"
                      v-model="form.address"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('address')"
                    >
                    <small
                      class="form-text text-muted transport-owner-helpers"
                      @click="copyFromJuristicAddress"
                      :disabled="formDisabled"
                    >
                      Совпадает с юридическим
                    </small>
                    <small v-if="errors.address" class="text-danger">{{ errors.address[0] }}</small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="phone">Телефон</label>
                    <phone-input
                      id="phone"
                      v-model="form.phone"
                      :disabled="formDisabled"
                      :placeholder = "'+7 812 345-67-89'"
                      @change="clearFieldError('phone')"
                    />
                    <small v-if="errors.phone" class="text-danger">{{ errors.phone[0] }}</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="position_of_manager">Должность руководителя</label>
                    <input
                      id="position_of_manager"
                      v-model="form.position_of_manager"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('position_of_manager')"
                    >
                    <small v-if="errors.position_of_manager" class="text-danger">{{ errors.position_of_manager[0]
                      }}</small>
                  </div>
                </div>
                <div class="col-md-8">
                  <div class="form-group">
                    <label for="name_of_manager">ФИО руководителя</label>
                    <input
                      id="name_of_manager"
                      v-model="form.name_of_manager"
                      type="text"
                      class="form-control"
                      :disabled="formDisabled"
                      @change="clearFieldError('name_of_manager')"
                    >
                    <small v-if="errors.name_of_manager" class="text-danger">{{ errors.name_of_manager[0] }}</small>
                  </div>
                </div>
              </div>
            </fieldset>

            <fieldset v-if="hasInn" class="documents panel-group">
              <h3>Договоры</h3>
              <hr>
              <div v-if="contracts.length" class="mb-4">
                <div
                  v-for="contract in visibleContracts"
                  :key="contract.id"
                  class="contract-item"
                >
                  <div class="row mb-2">
                    <div class="col-md-3">
                      <strong>№{{ contract.number }}</strong>
                    </div>
                    <div class="col-md-5">{{ contract.org_name }}</div>
                    <div class="col-md-2">ИНН: {{ contract.counterparty_inn }}</div>
                    <div class="col-md-2">
                      {{ formatDate(contract.start_date) }} — {{ formatDate(contract.end_date) || 'Бессрочный' }}
                    </div>
                  </div>
                  <uppy-uploader
                    v-model="contract.documents"
                    :dashboard-show-condition="false"
                    :document-id="contract.id"
                    :document-key="`contract-${contract.id}`"
                    :document-name="'Договор с субъектом транспортной инфраструктуры'"
                    :document-need-expire="false"
                    :document-short-name="'contract'"
                    :documents-show-condition="true"
                    :entity-id="contract.id"
                    :entity-type="'organization'"
                    :get-uuid="getUuid"
                    :mime-types="['image/*', 'application/pdf', '.jfif']"
                    :owner-type="'organization'"
                    :visible="true"
                  />
                </div>
                <button
                  v-if="contracts.length > contractsPreviewCount && !showAllContracts"
                  type="button"
                  class="btn btn-link p-0"
                  @click="showAllContracts = true"
                >
                  Показать ещё ({{ contracts.length - contractsPreviewCount }})
                </button>
                <button
                  v-if="showAllContracts"
                  type="button"
                  class="btn btn-link p-0"
                  @click="showAllContracts = false"
                >
                  Свернуть
                </button>
              </div>
              <div class="form-group" v-if="hasPermission('organization.modify_organizations')">
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="formDisabled"
                  @click="showContractForm = !showContractForm"
                >
                  {{ showContractForm ? 'Скрыть' : 'Добавить договор' }}
                </button>
              </div>
              <div v-if="showContractForm" class="contract-item mb-4">
                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Номер договора</label>
                      <input
                        v-model="form.contract.number"
                        type="text"
                        class="form-control"
                        :disabled="formDisabled"
                      >
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Дата договора</label>
                      <date-picker
                        v-model="form.contract.date"
                        format="DD.MM.YYYY"
                        value-type="YYYY-MM-DD"
                        :disabled="formDisabled"
                      />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Дата окончания договора</label>
                      <date-picker
                        v-model="form.contract.end_date"
                        format="DD.MM.YYYY"
                        value-type="YYYY-MM-DD"
                        :disabled="formDisabled || contractEndDateDisabled"
                      />
                      <label class="applicant-helpers flex-label">
                        <input
                          v-model="contractEndDateDisabled"
                          type="checkbox"
                          :disabled="formDisabled"
                          @change="form.contract.end_date = null"
                        />
                        &nbsp;
                        Бессрочный
                      </label>
                    </div>

                  </div>
                </div>
                <div class="row">
                  <div class="col-md-9">
                    <div class="form-group">
                      <organization-name-input
                        v-model="form.contract.organization"
                        input-label="Организация, с которой заключен договор"
                        input-id="form-contract-organization-name"
                        :disabled="formDisabled"
                        @typeahead-selected="selectContractTypeaheadOrganization($event)"
                      />
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label>ИНН</label>
                      <input
                        class="form-control"
                        v-model="form.contract.counterparty_inn"
                        :disabled="formDisabled"
                      />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <uppy-uploader
                        v-model="form.contract.documents"
                        :dashboard-show-condition="true"
                        :document-id="1"
                        :document-key="'contract'"
                        :document-name="'Договор с субъектом транспортной инфраструктуры'"
                        :document-need-expire="false"
                        :document-short-name="'contract'"
                        :documents-show-condition="false"
                        :entity-id="1"
                        :entity-type="'organization'"
                        :get-uuid="getUuid"
                        :mime-types="['image/*', 'application/pdf', '.jfif']"
                        :owner-type="'organization'"
                        :visible="true"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
            <fieldset v-if="canReviewDocs && isViewMode && docsStatus === 'under_review'" class="panel-group mt-4">
              <h3>Проверка документов</h3>
              <hr>

              <uppy-uploader
                v-for="docType in orgDocumentTypes"
                :key="docType.short_name"
                v-model="orgDocsByType[docType.short_name]"
                :dashboard-show-condition="isEditMode"
                :document-id="Number(docType.id)"
                :document-key="`org-doc-review-${docType.short_name}`"
                :document-name="docType.name"
                :document-need-expire="false"
                :document-short-name="docType.short_name"
                :documents-show-condition="!isEditMode"
                :entity-id="organizationId"
                :entity-type="'organization'"
                :get-uuid="getUuid"
                :mime-types="['image/*', 'application/pdf', '.jfif']"
                :owner-type="'organization'"
                :visible="false"
              />

              <div class="text-center" style="margin-top: 16px;">
                <button
                  type="button"
                  class="btn btn-success"
                  :disabled="submittingAccept || submittingReject"
                  @click="acceptDocuments"
                >
                  {{ submittingAccept ? 'Сохранение...' : 'Принять документы' }}
                </button>
                <button
                  type="button"
                  class="btn btn-danger"
                  style="margin-left: 8px;"
                  :disabled="submittingAccept || submittingReject"
                  v-b-modal="'reject-docs-modal-' + organizationId"
                >
                  Отклонить документы
                </button>
              </div>

              <b-modal
                :id="'reject-docs-modal-' + organizationId"
                title="Отклонение документов"
                ok-title="Отклонить"
                cancel-title="Отмена"
                ok-variant="danger"
                :ok-disabled="!rejectionNoteInput.trim() || submittingReject"
                @ok="rejectDocuments"
              >
                <div class="form-group">
                  <label>Примечание <span class="text-danger">*</span></label>
                  <textarea
                    v-model="rejectionNoteInput"
                    class="form-control"
                    rows="4"
                    placeholder="Укажите причину отклонения..."
                  ></textarea>
                </div>
              </b-modal>
            </fieldset>

            <fieldset v-if="canUploadDocs || (canReviewDocs && docsStatus !== 'under_review')" class="documents panel-group">
              <h3>Документы организации</h3>
              <hr>
              <uppy-uploader
                v-for="docType in orgDocumentTypes"
                :key="docType.short_name"
                v-model="orgDocsByType[docType.short_name]"
                :dashboard-show-condition="!['documents_accepted', 'under_review'].includes(docsStatus)"
                :document-id="docType.id"
                :document-key="`org-doc-${docType.short_name}`"
                :document-name="docType.name"
                :document-need-expire="false"
                :document-short-name="docType.short_name"
                :documents-show-condition="['documents_accepted', 'under_review'].includes(docsStatus)"
                :entity-id="organizationId"
                :entity-type="'organization'"
                :get-uuid="getUuid"
                :mime-types="['image/*', 'application/pdf', '.jfif']"
                :owner-type="'organization'"
                :visible="false"
              />
            </fieldset>

            <div v-if="canUploadDocs && isEditMode && docsStatus !== 'under_review' && docsStatus !== 'documents_accepted'" class="form-group text-center mt-3">
              <button
                type="button"
                class="btn btn-warning"
                :disabled="submittingForReview"
                @click="submitForReview"
              >
                {{ submittingForReview ? 'Отправка...' : 'Отправить на проверку' }}
              </button>
            </div>

            <div v-if="isViewMode" class="form-group text-center">
              <router-link
                :to="{ name: 'organization-edit', params: { id: organizationId } }"
                class="btn btn-primary"
              >
                Редактировать
              </router-link>
            </div>

            <div v-if="(!canUploadDocs && isEditMode) || (!isEditMode && !isViewMode)" class="form-group text-center">
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="disabled || submitting"
              >
                {{ submitting ? 'Сохранение...' : 'Сохранить' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@/common/assets/styles/phd-list.scss'
import mainMixin from '@/common/mixins/mainMixin'
import BaseSelect from '@/common/components/BaseSelect.vue'
import NationalityDropdown from '@/common/components/NationalityDropdown.vue'
import OrganizationNameInput from '@/common/components/OrganizationNameInput.vue'
import { ApiClient } from '@/common/helpers/api-client/ApiClient'
import UppyUploader from '@/common/components/inputs/UppyUploader.vue'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment-timezone'
import PhoneInput from '@/common/components/inputs/PhoneInput.vue'
import ContractOrgInnInput from '@/common/components/application/form-inputs/organization/ContractOrgInnInput.vue'
import permissionsMixin from '@/common/mixins/PermissionsMixin'

export default {
  name: 'OrganizationForm',

  mixins: [mainMixin, permissionsMixin],

  components: {
    ContractOrgInnInput,
    PhoneInput,
    UppyUploader,
    BaseSelect,
    NationalityDropdown,
    OrganizationNameInput
  },

  data() {
    return {
      contracts: [],
      submitting: false,
      submittingForReview: false,
      status: '',
      alertType: '',
      disabled: false,
      docsStatus: null,
      canUploadDocs: false,
      canReviewDocs: false,
      rejectionNote: null,
      orgDocumentTypes: [],
      orgDocsByType: {},
      rejectionNoteInput: '',
      submittingAccept: false,
      submittingReject: false,
      contractEndDateDisabled: false,
      locationName: 'all',
      errors: {},
      nationalitiesList: [],
      organizationId: null,
      showContractForm: false,
      showAllContracts: false,
      contractsPreviewCount: 3,
      regionsList: {
        77: 'г. Москва',
        78: 'Санкт-Петербург',
        50: 'Московская область'
      },
      subjects: [
        {
          id: 'entity',
          name: 'Юридическое лицо'
        },
        {
          id: 'fiz',
          name: 'Физическое лицо'
        },
        {
          id: 'individual',
          name: 'Индивидуальный предприниматель'
        },
        {
          id: 'terminal',
          name: 'Терминал'
        }
      ],
      form: {
        subject_id: 'entity',
        nationality_id: 182,
        name: '',
        full_name: '',
        ogrn: '',
        inn: '',
        kpp: '',
        registration_date: '',
        okved: '',
        okpo: '',
        juristic_address: '',
        region_id: '',
        address: '',
        phone: '',
        position_of_manager: '',
        name_of_manager: '',
        reason: 'charter',
        requisites_reason: '',
        documents: [],
        contract: {
          number: '',
          date: '',
          end_date: '',
          organization: '',
          counterparty_inn: '',
          documents: []
        }
      }
    }
  },

  computed: {
    isEditMode() {
      return this.$route.name === 'organization-edit'
    },

    isViewMode() {
      return this.$route.name === 'organization-view'
    },

    formDisabled() {
      return this.disabled || this.canUploadDocs || this.isViewMode
    },

    hasInn() {
      return !!this.form.inn
    },
    visibleContracts() {
      if (this.showAllContracts) return this.contracts
      return this.contracts.slice(0, this.contractsPreviewCount)
    },
    submitForm() {
      const formatFile = file => ({
        meta: {
          checked: file.meta.checked,
          date_of_expire: file.meta.date_of_expire?.isValid() ? file.meta.date_of_expire.format('YYYY-MM-DD') : null,
          number: file.meta.number ?? null,
          filename: file.meta.uuid_any + '.' + this.getExtension(file.meta.filename),
          uuid_any: file.meta.uuid_any
        }
      })

      const formattedDocuments = {}
      for (const key in this.form.documents) {
        formattedDocuments[key] = this.form.documents[key].map(formatFile)
      }
      const documents = formattedDocuments

      let formattedContract = {
        ...this.form.contract,
        documents: this.form.contract.documents ? this.form.contract.documents.map(formatFile) : []
      }
      if (this.showContractForm === false && this.form.contract.number === '') {
        formattedContract = []
      }

      return { ...this.form, documents, formattedContract }
    },
    pageTitle() {
      if (this.isViewMode) return 'Просмотр организации'
      return this.isEditMode ? 'Редактирование организации' : 'Создание организации'
    },

    nameLabel() {
      return this.form.subject_id === 'fiz' ? 'Фамилия, имя, отчество' : 'Наименование организации'
    },

    disabledName() {
      return this.formDisabled || this.form.subject_id === 'fiz'
    }
  },

  async created() {
    this.checkEditMode()
    await this.fetchData()
    if (this.isEditMode || this.isViewMode) {
      await this.loadOrganization()
    }
    this.hidePreloader()
  },

  methods: {
    formatDate(date) {
      return moment.tz(date, 'YYYY-MM-DD', this.timezone).format('DD.MM.YYYY')
    },
    getExtension(file) {
      return file.split('.').pop()
    },
    getUuid() {
      return uuidv4()
    },
    handleNameChange(value) {
      this.cleanContactFields()
      this.clearFieldError('name')
    },

    clearFieldError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field)
      }
    },

    cleanContactFields() {
      // this.form.phone = null
      this.form.email = null
    },

    switchLocationName() {
      this.locationName = this.locationName !== 'all' ? 'all' : 'nw'
    },

    selectTypeaheadOrganization(item) {
      if (this.form.subject_id === 'fiz') {
        if (item instanceof Object) {
          this.form.name = item.value
        }
        return
      }

      if (item instanceof Object) {
        this.form.name = item.short_name || item.value || ''
        this.form.full_name = item.org_name || item.value
        this.form.inn = item.inn || ''
        this.form.ogrn = item.ogrn || ''
        this.form.kpp = item.kpp || ''
        this.form.registration_date = item.registration_date || ''
        this.form.okved = item.okved || ''
        this.form.okpo = item.okpo || ''
        this.form.juristic_address = item.juristic_address || item.address || ''
        this.form.address = item.juristic_address || item.address || ''
        this.form.phone = item.phone || ''
        this.form.position_of_manager = item.position_of_manager || ''
        this.form.name_of_manager = item.director_name || ''
        this.form.region_id = item.region_id || ''
      }
    },

    selectContractTypeaheadOrganization(item) {
      if (this.form.subject_id === 'fiz') {
        if (item instanceof Object) {
          this.form.name = item.value
          this.form.contract.counterparty_inn = item.inn || ''
        }
        return
      }

      if (item instanceof Object) {
        this.form.contract.organization = item.org_name || item.short_name || item.value || ''
        this.form.contract.counterparty_inn = item.inn || ''
      }
    },

    checkEditMode() {
      const organizationId = this.$route.query.id || this.$route.params.id
      if (organizationId) {
        this.organizationId = organizationId
      }
    },

    async fetchData() {
      try {
        const [regionsResponse, docTypesResponse] = await Promise.all([
          ApiClient.getRegions(),
          ApiClient.getDocumentTypes('organization')
        ])
        this.regionsList = regionsResponse?.data?.data?.regions ?? this.regionsList
        let types = docTypesResponse?.data?.data ?? []
        const byType = {}
        types = types.filter(t => t.short_name !== 'contract')
        types.forEach(t => { byType[t.short_name] = [] })
        this.orgDocumentTypes = types
        this.orgDocsByType = byType
      } catch (error) {
        console.error('Error loading organization data:', error)
      }
    },

    async loadOrganization() {
      try {
        const response = await ApiClient.getOrganization(this.organizationId)
        if (response.status === 404) {
          this.status = 'Организация не найдена'
          this.alertType = 'error'
          setTimeout(() => {
            this.$router.push({ name: 'organization-list' })
          }, 1500)
        }
        const organization = response?.data?.data
        if (organization) {
          this.$set(this, 'form', {
            subject_id: organization.subject_id || 'entity',
            nationality_id: organization.nationality_id || 182,
            name: organization.short_name || '',
            full_name: organization.name || '',
            ogrn: organization.ogrn || '',
            inn: organization.inn || '',
            kpp: organization.kpp || '',
            registration_date: organization.registration_date || '',
            okved: organization.okved || '',
            okpo: organization.okpo || '',
            juristic_address: organization.juristic_address || '',
            region_id: organization.region_id || '',
            address: organization.address || '',
            phone: organization.phone || '',
            position_of_manager: organization.position_of_manager || '',
            name_of_manager: organization.director_name || '',
            reason: organization.reason || 'charter',
            requisites_reason: organization.requisites_reason || '',
            documents: organization.documents,
            contract: {
              number: '',
              date: '',
              end_date: '',
              organization: '',
              counterparty_inn: '',
              documents: []
            }
          })
          this.contracts = organization.contracts || []
          this.docsStatus = organization.docs_status || null
          this.canUploadDocs = organization.can_upload_docs || false
          this.canReviewDocs = organization.can_review_docs || false
          this.rejectionNote = organization.rejection_note || null

          if (organization.org_docs_by_type) {
            const merged = { ...this.orgDocsByType }
            for (const [key, docs] of Object.entries(organization.org_docs_by_type)) {
              merged[key] = docs || []
            }
            this.$set(this, 'orgDocsByType', merged)
          }
        }
      } catch (error) {
        console.error('Error loading organization:', error)
        this.status = 'Ошибка при загрузке данных организации'
        this.alertType = 'error'
      }
    },

    copyFromJuristicAddress() {
      this.form.address = this.form.juristic_address
    },

    handleValidationErrors(serverErrors) {
      if (!serverErrors) return
      // Map server field name to form field name
      if (serverErrors.director_name && !serverErrors.name_of_manager) {
        serverErrors.name_of_manager = serverErrors.director_name
        delete serverErrors.director_name
      }
      this.errors = serverErrors
      this.status = 'Пожалуйста, исправьте ошибки в форме'
      this.alertType = 'error'
      this.$nextTick(() => {
        const firstError = document.querySelector('.text-danger')
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },

    async runDocAction(submittingFlag, apiCall, onSuccess, errorMessage) {
      try {
        this[submittingFlag] = true
        this.status = ''
        this.alertType = ''
        const response = await apiCall()
        if (response.status === 200) {
          onSuccess(response)
        } else {
          this.status = response.data?.message || errorMessage
          this.alertType = 'error'
        }
      } catch (error) {
        console.error(error)
        this.status = errorMessage
        this.alertType = 'error'
      } finally {
        this[submittingFlag] = false
      }
    },

    acceptDocuments() {
      this.runDocAction(
        'submittingAccept',
        () => ApiClient.acceptOrganizationDocuments(this.organizationId),
        () => {
          this.docsStatus = 'documents_accepted'
          this.status = 'Документы приняты'
          this.alertType = 'success'
        },
        'Ошибка при принятии документов'
      )
    },

    async rejectDocuments(modalEvent) {
      if (modalEvent && modalEvent.preventDefault) {
        modalEvent.preventDefault()
      }
      const note = this.rejectionNoteInput.trim()
      if (!note) return
      this.runDocAction(
        'submittingReject',
        () => ApiClient.rejectOrganizationDocuments(this.organizationId, note),
        () => {
          this.docsStatus = 'clarification_required'
          this.rejectionNote = note
          this.rejectionNoteInput = ''
          this.$bvModal.hide('reject-docs-modal-' + this.organizationId)
          this.status = 'Документы отклонены'
          this.alertType = 'error'
        },
        'Ошибка при отклонении документов'
      )
    },

    formatOrgDocsForReview() {
      const result = {}
      for (const [docType, files] of Object.entries(this.orgDocsByType)) {
        result[docType] = (files || []).map(file => ({
          meta: {
            filename: file.meta.uuid_any + '.' + this.getExtension(file.meta.filename),
            uuid_any: file.meta.uuid_any
          }
        }))
      }
      return result
    },

    submitForReview() {
      this.runDocAction(
        'submittingForReview',
        () => ApiClient.submitOrganizationForReview(this.organizationId, this.formatOrgDocsForReview()),
        () => {
          this.docsStatus = 'under_review'
          this.status = 'Документы отправлены на проверку'
          this.alertType = 'success'
        },
        'Ошибка при отправке на проверку'
      )
    },

    async onSubmit() {
      try {
        this.submitting = true
        this.status = ''
        this.alertType = ''
        this.errors = {}

        let response;

        if (this.isEditMode) {
          response = await ApiClient.updateOrganization(this.organizationId, this.submitForm)
        } else {
          response = await ApiClient.createOrganization(this.submitForm)
        }

        if (response.status === 200) {
          this.status = 'Данные успешно сохранены'
          this.alertType = 'success'
          setTimeout(() => {
            this.$router.push({ name: 'organization-list' })
          }, 1500)
        } else if (response.status === 422) {
          // ApiClient не бросает ошибку на 422 — обрабатываем по статусу
          this.handleValidationErrors(response.data?.data?.errors)
        } else {
          this.status = 'Ошибка при сохранении данных'
          this.alertType = 'error'
        }
      } catch (error) {
        console.error('Error saving organization:', error)

        // На случай если всё же попадёт в catch
        const serverErrors = error.response?.data?.data?.errors
        if (serverErrors) {
          this.handleValidationErrors(serverErrors)
        } else {
          this.status = 'Ошибка при сохранении данных'
          this.alertType = 'error'
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.contract-item {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
}
</style>
